import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import SEOHead from '@/components/SEOHead';

const BADGE_SIZES = [
  { label: 'Small', width: 160, height: 52 },
  { label: 'Medium', width: 200, height: 65 },
  { label: 'Large', width: 250, height: 82 },
];

function CreatorBadge() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState('dark');
  const [size, setSize] = useState(1);
  const [storeUrl, setStoreUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const badgeSrc = `https://theailedger.ai/badge-${theme}.png`;
  const linkUrl = storeUrl.trim() || 'https://theailedger.ai';
  const { width, height } = BADGE_SIZES[size];

  const embedCode = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" title="Available on theailedger.ai"><img src="${badgeSrc}" alt="Available on theailedger.ai" width="${width}" height="${height}" style="border:0;" /></a>`;

  const markdownCode = `[![Available on theailedger.ai](${badgeSrc})](${linkUrl})`;

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEOHead
        title="Creator Badge - Embed on Your Website"
        description="Get your free 'Available on theailedger.ai' badge to embed on your website. Drive traffic to your AI product store and show customers where to buy."
        path="/badge"
        keywords="theailedger badge, AI marketplace badge, embed badge, creator badge, available on theailedger"
      />

      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-12 h-14 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-[#00F0FF] hover:text-[#00C2CC]" data-testid="back-button">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-12 py-12 max-w-3xl">
        {/* Title */}
        <div className="text-center mb-10">
          <Logo className="w-10 h-10 mx-auto mb-4" color="#00F0FF" />
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Creator Badge</h1>
          <p className="text-neutral-400 max-w-lg mx-auto">
            Embed this badge on your website to let visitors know your AI product is available on theailedger.ai. Every badge links back to your store.
          </p>
        </div>

        {/* Badge Preview */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 mb-8" data-testid="badge-preview-section">
          <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4">Preview</h2>
          <div className={`flex items-center justify-center rounded-xl p-10 mb-6 ${theme === 'dark' ? 'bg-white' : 'bg-neutral-900'}`}>
            <img
              src={`/badge-${theme}.png`}
              alt="Available on theailedger.ai"
              width={BADGE_SIZES[size].width}
              height={BADGE_SIZES[size].height}
              className="object-contain"
              data-testid="badge-preview-image"
            />
          </div>

          {/* Controls */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {/* Theme */}
            <div>
              <label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2 block">Theme</label>
              <div className="flex gap-2">
                <button onClick={() => setTheme('dark')} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${theme === 'dark' ? 'bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/40' : 'bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10'}`} data-testid="theme-dark">Dark</button>
                <button onClick={() => setTheme('light')} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${theme === 'light' ? 'bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/40' : 'bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10'}`} data-testid="theme-light">Light</button>
              </div>
            </div>

            {/* Size */}
            <div>
              <label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2 block">Size</label>
              <div className="flex gap-2">
                {BADGE_SIZES.map((s, i) => (
                  <button key={s.label} onClick={() => setSize(i)} className={`flex-1 py-2 px-2 rounded-lg text-sm font-medium transition-all ${size === i ? 'bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/40' : 'bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10'}`} data-testid={`size-${s.label.toLowerCase()}`}>{s.label}</button>
                ))}
              </div>
            </div>

            {/* Store URL */}
            <div>
              <label className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2 block">Your Store URL</label>
              <input
                type="text"
                placeholder="theailedger.ai/store/your-store"
                value={storeUrl}
                onChange={(e) => setStoreUrl(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-[#00F0FF]/50"
                data-testid="store-url-input"
              />
            </div>
          </div>
        </div>

        {/* HTML Embed Code */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 mb-6" data-testid="html-embed-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider">HTML Embed Code</h2>
            <Button size="sm" variant="outline" onClick={() => handleCopy(embedCode)} className="border-white/10 text-xs h-7" data-testid="copy-html-button">
              {copied ? <Check className="w-3 h-3 mr-1 text-green-400" /> : <Copy className="w-3 h-3 mr-1" />}
              Copy
            </Button>
          </div>
          <pre className="bg-black/60 rounded-xl p-4 text-sm text-[#00F0FF] overflow-x-auto whitespace-pre-wrap break-all font-mono leading-relaxed" data-testid="html-code-block">{embedCode}</pre>
        </div>

        {/* Markdown Code */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 mb-10" data-testid="markdown-embed-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider">Markdown</h2>
            <Button size="sm" variant="outline" onClick={() => handleCopy(markdownCode)} className="border-white/10 text-xs h-7" data-testid="copy-markdown-button">
              <Copy className="w-3 h-3 mr-1" />
              Copy
            </Button>
          </div>
          <pre className="bg-black/60 rounded-xl p-4 text-sm text-white overflow-x-auto whitespace-pre-wrap break-all font-mono leading-relaxed" data-testid="markdown-code-block">{markdownCode}</pre>
        </div>

        {/* Instructions */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8">
          <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4">How to Use</h2>
          <ol className="space-y-3 text-sm text-neutral-300">
            <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] flex items-center justify-center text-xs font-bold">1</span><span>Enter your store URL above (e.g., <code className="text-[#00F0FF]">https://theailedger.ai/store/your-store</code>)</span></li>
            <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] flex items-center justify-center text-xs font-bold">2</span><span>Choose your preferred theme (dark or light) and badge size</span></li>
            <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] flex items-center justify-center text-xs font-bold">3</span><span>Copy the HTML embed code and paste it into your website's HTML</span></li>
            <li className="flex gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] flex items-center justify-center text-xs font-bold">4</span><span>For GitHub READMEs or markdown pages, use the Markdown code instead</span></li>
          </ol>
          <p className="mt-4 text-xs text-neutral-500">
            The badge links visitors directly to your store on theailedger.ai, helping drive traffic and sales to your AI products.
          </p>
        </div>
      </div>
    </div>
  );
}

export default CreatorBadge;
